// Identifier formats (03 §3.5). ULID generation lives outside the domain: these functions
// only build and validate strings.

export const PREFIXES = [
  'cmd',
  'cus',
  'con',
  'pur',
  'run',
  'stp',
  'trp',
  'ctn',
  'exc',
  'pay',
  'bil',
  'clm',
  'mov',
  'evt',
  'ntf',
  'til',
  'ptn',
  'hnd',
  'pup',
  'alr',
  'tkt',
  'cmp',
  'wlt',
  'apv',
  'adv',
  'ici',
  'dch',
  'xfr',
  'dev',
  'season',
  // Phase 7: refunds, shipment changes, customer merges, destination charges
  'rfd',
  'shc',
  'mrg',
  'chg',
  // Finance: a general transfer between two countries (14 §14.9)
  'ict',
  // Numbering (03 §3.5): a tracking number series of a customer or agent, and an agent
  'nsr',
  'agt',
  // A document attached to a container or a shipment (03 §3.4a)
  'doc',
] as const;

export type IdPrefix = (typeof PREFIXES)[number];

const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function isUlid(value: string): boolean {
  return ULID_PATTERN.test(value);
}

export function formatPrefixedId(prefix: IdPrefix, ulid: string): string {
  if (!PREFIXES.includes(prefix)) throw new Error(`Unknown ID prefix: ${prefix}`);
  if (!isUlid(ulid)) throw new Error(`Not a ULID (26 Crockford base32 characters): ${ulid}`);
  return `${prefix}_${ulid}`;
}

export function isPrefixedId(prefix: IdPrefix, value: string): boolean {
  return value.startsWith(`${prefix}_`) && isUlid(value.slice(prefix.length + 1));
}

export function parsePrefixedId(value: string): { prefix: IdPrefix; ulid: string } | null {
  const prefix = PREFIXES.find((candidate) => isPrefixedId(candidate, value));
  return prefix === undefined ? null : { prefix, ulid: value.slice(prefix.length + 1) };
}

// ------------------------------------------------------------------- Luhn

function assertDigits(digits: string): void {
  if (!/^\d+$/.test(digits)) throw new Error(`Expected digits only, got "${digits}"`);
}

/** Luhn mod-10 sum where the rightmost digit is doubled when `doubleFirst` is set. */
function luhnSum(digits: string, doubleFirst: boolean): number {
  let sum = 0;
  let double = doubleFirst;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = Number(digits[i]);
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum;
}

export function luhnCheckDigit(digits: string): number {
  assertDigits(digits);
  return (10 - (luhnSum(digits, true) % 10)) % 10;
}

/** `digitsWithCheck` is the payload followed by its check digit. */
export function isLuhnValid(digitsWithCheck: string): boolean {
  return /^\d{2,}$/.test(digitsWithCheck) && luhnSum(digitsWithCheck, false) % 10 === 0;
}

// ------------------------------------------------------- numbered documents

function pad(value: number, width: number, label: string): string {
  if (!Number.isInteger(value) || value < 0 || value >= 10 ** width) {
    throw new Error(`${label} must be an integer from 0 to ${10 ** width - 1}, got ${value}`);
  }
  return String(value).padStart(width, '0');
}

function twoDigitYear(year: number): string {
  if (!Number.isInteger(year) || year < 2000 || year > 2099) {
    throw new Error(`Year must be between 2000 and 2099, got ${year}`);
  }
  return pad(year % 100, 2, 'Year');
}

function assertBranch(branch: string): void {
  if (!/^[A-Z]{3,4}$/.test(branch))
    throw new Error(`Branch code must be 3-4 uppercase letters, got "${branch}"`);
}

export interface SeriesNumberParts {
  readonly branch: string;
  /** Full year, 2000-2099 (the number only carries the last two digits). */
  readonly year: number;
  readonly seq: number;
}

function makeSeriesNumber(lead: string, { branch, year, seq }: SeriesNumberParts): string {
  assertBranch(branch);
  const digits = twoDigitYear(year) + pad(seq, 6, 'Sequence');
  return `${lead}${branch}${digits}${luhnCheckDigit(digits)}`;
}

function parseSeriesNumber(pattern: RegExp, value: string): SeriesNumberParts | null {
  const match = pattern.exec(value);
  if (!match) return null;
  const [, branch = '', yy = '', seq = '', check = ''] = match;
  if (luhnCheckDigit(yy + seq) !== Number(check)) return null;
  return { branch, year: 2000 + Number(yy), seq: Number(seq) };
}

const TRACKING_PATTERN = /^([A-Z]{3,4})(\d{2})(\d{6})(\d)$/;
const RECEIPT_PATTERN = /^R([A-Z]{3,4})(\d{2})(\d{6})(\d)$/;

/** `{BRANCH}{YY}{SEQ6}{CHECK}`, check digit over YY+SEQ6 only. */
export function makeTrackingNumber(parts: SeriesNumberParts): string {
  return makeSeriesNumber('', parts);
}

export function parseBranchTrackingNumber(value: string): SeriesNumberParts | null {
  return parseSeriesNumber(TRACKING_PATTERN, value);
}

/** `R{BRANCH}{YY}{SEQ6}{CHECK}` */
export function makeReceiptNumber(parts: SeriesNumberParts): string {
  return makeSeriesNumber('R', parts);
}

export function parseReceiptNumber(value: string): SeriesNumberParts | null {
  return parseSeriesNumber(RECEIPT_PATTERN, value);
}

/** `EB{SEQ9}{CHECK}` */
export function makeBoxBarcode(seq: number): string {
  const digits = pad(seq, 9, 'Sequence');
  return `EB${digits}${luhnCheckDigit(digits)}`;
}

export function parseBoxBarcode(value: string): { seq: number } | null {
  const match = /^EB(\d{9})(\d)$/.exec(value);
  const [, seq = '', check = ''] = match ?? [];
  return match && luhnCheckDigit(seq) === Number(check) ? { seq: Number(seq) } : null;
}

export interface TaxInvoiceParts {
  readonly entity: string;
  readonly year: number;
  readonly seq: number;
}

/** `{ENTITY}-{YYYY}-{SEQ7}`, gap-free per entity (assigned by the server sequencer). */
export function makeTaxInvoiceNumber({ entity, year, seq }: TaxInvoiceParts): string {
  if (!/^[A-Z]{2,6}$/.test(entity))
    throw new Error(`Entity must be 2-6 uppercase letters, got "${entity}"`);
  if (!Number.isInteger(year) || year < 2000 || year > 2099)
    throw new Error(`Invalid year: ${year}`);
  return `${entity}-${year}-${pad(seq, 7, 'Sequence')}`;
}

export function parseTaxInvoiceNumber(value: string): TaxInvoiceParts | null {
  const match = /^([A-Z]{2,6})-(\d{4})-(\d{7})$/.exec(value);
  return match ? { entity: match[1] ?? '', year: Number(match[2]), seq: Number(match[3]) } : null;
}

// ----------------------------------------------------------------- safety

const MAX_DOC_ID_LENGTH = 150;

const UNSAFE_CHARACTERS = new Set(['/', '\\', '.', '%']);

function isSafeCharacter(character: string): boolean {
  const code = character.codePointAt(0) ?? 0;
  return code > 0x1f && code !== 0x7f && !UNSAFE_CHARACTERS.has(character);
}

/**
 * True when a string is safe to use as one Firestore/Storage path segment. Rejects `/`, `\`, `.`
 * (so no `..`), `%` (encoded traversal), whitespace and control characters (07 §7.4).
 */
export function isSafeDocId(value: string): boolean {
  return (
    value.length > 0 &&
    value.length <= MAX_DOC_ID_LENGTH &&
    !/\s/u.test(value) &&
    [...value].every(isSafeCharacter)
  );
}
